import * as M from "./movement.mjs";

function clone(st) { return structuredClone(st); }

function oreTiles(st) {
  const out = [];
  for (const [k, id] of Object.entries(st.tiles ?? {})) {
    if (id !== "ore") continue;
    const [q, r] = k.split(",").map(Number);
    out.push({ q, r, owner: st.oreOwner?.[k] ?? 0 });
  }
  return out;
}

function ownHq(st, p) {
  return (st.bases ?? []).find((b) => b.owner === p && (b.hp ?? 0) > 0) || null;
}

function enemyHq(st, p) {
  return (st.bases ?? []).find((b) => b.owner !== p && (b.hp ?? 0) > 0) || null;
}

function hqThreatened(st, p) {
  return M.threatCells(st, p).some((t) => t.kind === "base" && t.targetBase === p);
}

function nearestThreat(st, p, from) {
  let hit = null, best = Infinity;
  for (const t of M.threatCells(st, p)) {
    if (t.kind !== "base" || t.targetBase !== p) continue;
    const src = M.byId(st, t.from);
    if (!src) continue;
    const d = M.dist(from, src);
    if (d < best) { best = d; hit = src; }
  }
  return hit;
}

function capturableOre(st, owner, self) {
  return oreTiles(st).filter((o) => {
    if (o.owner === owner) return false;
    const occ = M.unitAt(st, o);
    if (occ && occ.id !== self?.id) return false;
    return true;
  });
}

function harvestOre(st, owner) {
  return oreTiles(st).filter((o) => o.owner === 0 || o.owner === owner);
}

function bestDestination(st, u, scoreFn) {
  const spots = M.reachableHexes(st, u.id);
  let best = null, bestS = -Infinity;
  for (const s of spots) {
    const sc = scoreFn(s);
    if (sc > bestS) { bestS = sc; best = s; }
  }
  return best;
}

function minDist(hex, list) {
  let n = Infinity;
  for (const t of list) n = Math.min(n, M.dist(hex, t));
  return n;
}

function scoreInfantry(st, u, hex) {
  const ores = capturableOre(st, u.owner, u);
  if (!ores.length) return -M.dist(hex, enemyHq(st, u.owner) || hex);
  const on = ores.find((o) => o.q === hex.q && o.r === hex.r);
  if (on) return 200 - hex.cost;
  return 80 - minDist(hex, ores) * 12 - hex.cost;
}

function scoreHarvester(st, u, hex) {
  const ores = harvestOre(st, u.owner);
  if (!ores.length) return -hex.cost;
  const on = ores.find((o) => o.q === hex.q && o.r === hex.r);
  if (on) return 180 - hex.cost;
  return 70 - minDist(hex, ores) * 10 - hex.cost;
}

function scoreArtillery(st, u, hex) {
  const foes = [...st.units.filter((t) => t.owner !== u.owner), ...((st.bases ?? []).filter((b) => b.owner !== u.owner && (b.hp ?? 0) > 0))];
  if (!foes.length) return 0;
  let best = -Infinity;
  for (const f of foes) {
    const d = M.dist(hex, f);
    const inBand = d >= 3 && d <= 4 ? 40 : -Math.abs(d - 3.5) * 8;
    best = Math.max(best, inBand);
  }
  // A plateau firing position adds RULES.highGround to every shot.
  return best + (M.levelOf(st, hex) > 0 ? 12 : 0) - hex.cost;
}

function scoreTank(st, u, hex) {
  const hq = ownHq(st, u.owner);
  if (hq && hqThreatened(st, u.owner)) {
    const threat = nearestThreat(st, u.owner, hex) || hq;
    return 120 - M.dist(hex, threat) * 14 - hex.cost;
  }
  const goal = enemyHq(st, u.owner) || { q: hex.q, r: hex.r };
  const foes = st.units.filter((t) => t.owner !== u.owner);
  const prey = foes.length ? minDist(hex, foes) : M.dist(hex, goal);
  return 40 - prey * 8 - M.dist(hex, goal) * 3 + (M.levelOf(st, hex) > 0 ? 6 : 0) - hex.cost;
}

function scoreShip(st, u, hex) {
  const hq = enemyHq(st, u.owner);
  const docks = (st.structures ?? []).filter((s) => s.owner !== u.owner && s.type === "dock");
  const coasts = hq ? M.DIRS.map((d) => M.add(hq, d)).filter((h) => M.inBounds(st, h) && M.terrainId(st, h) === "water") : [];
  const goals = docks.length ? docks : coasts.length ? coasts : hq ? [hq] : [hex];
  return 70 - minDist(hex, goals) * 7 - hex.cost;
}

function destinationScore(st, u, hex) {
  if (u.type === "infantry") return scoreInfantry(st, u, hex);
  if (u.type === "harvester") return scoreHarvester(st, u, hex);
  if (u.type === "artillery") return scoreArtillery(st, u, hex);
  if (M.isNaval(u)) return scoreShip(st, u, hex);
  return scoreTank(st, u, hex);
}

function stayScore(st, u) {
  return destinationScore(st, u, { q: u.q, r: u.r, cost: 0, hexes: 0 });
}

export function collectAttacks(st) {
  const p = st.turn.player;
  const out = [];
  for (const u of st.units) {
    if (u.owner !== p || u.fresh || u.attacked) continue;
    out.push(...M.attackTargets(st, u.id).map((t) => t.plan));
  }
  out.sort((a, b) => b.damage - a.damage);
  return out;
}

function fireReady(st, log) {
  for (const plan of collectAttacks(st)) {
    if (M.winner(st) !== null) break;
    if (plan.cost > st.turn.ap) continue;
    const live = M.byId(st, plan.attacker);
    if (!live || live.attacked) continue;
    if (plan.target.unitId && !M.byId(st, plan.target.unitId)) continue;
    const again = M.planAttack(st, plan.attacker, plan.target);
    if (!again.ok || again.cost > st.turn.ap) continue;
    const t = again.target;
    const def = t.base !== undefined ? st.bases.find((b) => b.owner === t.base)
      : t.structureId !== undefined ? M.structureById(st, t.structureId) : M.byId(st, t.unitId);
    const from = { q: live.q, r: live.r }, to = { q: def.q, r: def.r }, weapon = live.type;
    const toAir = t.unitId !== undefined && M.isAir(def), toType = def.type ?? "base";
    const res = M.applyAttack(st, again);
    log.push({ kind: "attack", attacker: again.attacker, damage: res.damage, destroyed: res.destroyed, from, to, weapon, toAir, toType, targetId: t.unitId });
  }
}

function tryCoordinated(st, log) {
  const p = st.turn.player;
  for (const u of st.units) {
    if (u.owner !== p) continue;
    const w = M.wedgeAt(st, u);
    if (!w) continue;
    const ids = [w.lead, ...w.wings];
    const plan = M.planGroupMove(st, ids, u.facing);
    if (plan.ok && plan.discount === "COORDINATED_ADVANCE" && plan.cost <= st.turn.ap) {
      M.apply(st, plan);
      log.push({ kind: "group", ids, discount: plan.discount });
      return true;
    }
  }
  return false;
}

function shouldHold(st, u) {
  if (u.type === "artillery") return M.attackTargets(st, u.id).length > 0;
  if (u.type === "infantry") {
    const ores = capturableOre(st, u.owner, u);
    return ores.some((o) => o.q === u.q && o.r === u.r);
  }
  if (u.type === "harvester") {
    return harvestOre(st, u.owner).some((o) => o.q === u.q && o.r === u.r);
  }
  return false;
}

function march(st, log) {
  const p = st.turn.player;
  const movers = st.units.filter((u) => u.owner === p && (u.moved ?? 0) < M.moveCap(st, u));
  movers.sort((a, b) => {
    // Armour, ships and aircraft lead the push; infantry follows to claim ground; harvesters last.
    const rank = (u) => (u.type === "tank" || M.isAir(u) || M.isNaval(u) ? 0 : u.type === "infantry" ? 1 : u.type === "artillery" ? 2 : 3);
    return rank(a) - rank(b);
  });
  // Keep AP back for shots after the advance when the armies are in contact.
  const foes = st.units.filter((u) => u.owner !== p);
  const contact = st.units.some((u) => u.owner === p && foes.some((f) => M.dist(u, f) <= 5));
  const reserve = contact ? Math.min(2, st.turn.ap) : 0;
  for (const id of movers.map((u) => u.id)) {
    if (st.turn.ap - reserve <= 0 || M.winner(st) !== null) break;
    const u = M.byId(st, id);
    if (!u || shouldHold(st, u)) continue;
    const dest = bestDestination(st, u, (hex) => destinationScore(st, u, hex));
    if (!dest || dest.cost > st.turn.ap - reserve) continue;
    if (destinationScore(st, u, dest) <= stayScore(st, u)) continue;
    const plan = M.planMove(st, u.id, dest.path);
    if (plan.ok && plan.cost <= st.turn.ap) {
      M.apply(st, plan);
      log.push({ kind: "move", id: u.id, to: { q: dest.q, r: dest.r } });
    }
  }
}

const credits = (st, p) => st.players?.[p]?.credits ?? 0;

// Research when the next tier is affordable with change to spare; tech edge multiplies every shot.
function maybeResearch(st, log) {
  const p = st.turn.player, plan = M.planResearch(st);
  if (!plan.ok || st.turn.ap < plan.cost + 2 || credits(st, p) < plan.credits + 400) return;
  M.applyResearch(st, plan);
  log.push({ kind: "research", tech: plan.tech });
}

function hasSea(st) {
  return M.domainAllowed(st, "sea") && Object.values(st.tiles ?? {}).includes("water");
}

// Tower-defense layer: dock if the map has sea, turrets toward enemies, AA once aircraft appear, coastal guns vs ships.
function maybeDefend(st, log) {
  const p = st.turn.player, hq = ownHq(st, p);
  if (!hq) return;
  const mine = (type) => (st.structures ?? []).filter((x) => x.owner === p && x.type === type).length;
  const foes = st.units.filter((u) => u.owner !== p);
  const air = foes.filter((u) => M.isAir(u));
  const ships = foes.filter((u) => M.isNaval(u));
  const build = (type, near) => {
    const spots = M.buildHexes(st, p, type).filter((h) => M.dist(h, hq) >= 2);
    if (!spots.length) return false;
    spots.sort((a, b) => M.dist(a, near) - M.dist(b, near));
    const plan = M.planBuild(st, type, spots[0]);
    if (!plan.ok) return false;
    M.applyBuild(st, plan);
    log.push({ kind: "build", type, to: plan.to });
    return true;
  };
  if (hasSea(st) && mine("dock") < 1) build("dock", hq);
  if (hasSea(st) && (ships.length || mine("dock")) && mine("coastal") < 2) {
    build("coastal", ships[0] || hq);
  }
  if (air.length && mine("aa") < 2) build("aa", air.sort((a, b) => M.dist(a, hq) - M.dist(b, hq))[0]);
  const close = foes.filter((u) => M.dist(u, hq) <= 8).sort((a, b) => M.dist(a, hq) - M.dist(b, hq));
  if (close.length && mine("turret") < 4) build("turret", close[0]);
}

function maybeProduce(st, log, keepAp = 0) {
  const p = st.turn.player;
  if (st.turn.ap - keepAp < M.UNIT_AP_COST.infantry) return;
  if (credits(st, p) < M.UNIT_COSTS.infantry) return;
  const mine = st.units.filter((u) => u.owner === p);
  const theirs = st.units.filter((u) => u.owner !== p).length;
  const inf = mine.filter((u) => u.type === "infantry").length;
  const tanks = mine.filter((u) => u.type === "tank").length;
  const aircraft = mine.filter((u) => M.isAir(u)).length;
  const ships = mine.filter((u) => M.isNaval(u)).length;
  if (mine.length > theirs + 1 && inf > 0 && credits(st, p) < 2000) return;
  // Best unit the treasury, tech and map allow: ships on sea maps, then jets, helicopters, tanks, infantry.
  const order = [];
  if (hasSea(st) && (st.structures ?? []).some((s) => s.owner === p && s.type === "dock") && ships < 4) {
    order.push("battleship", "destroyer", "gunboat");
  }
  if (aircraft < 4) order.push("jet", "helicopter");
  if (tanks * 2 < inf) order.push("tank");
  order.push("infantry");
  for (const type of order) {
    const prod = M.planProduce(st, type);
    if (!prod.ok || st.turn.ap - prod.cost < keepAp) continue;
    M.applyProduce(st, prod);
    log.push({ kind: "produce", type });
    return;
  }
}

/** Simulate a full AI turn. Never spends more AP than available. Returns applied action labels. */
export function playAiTurn(st) {
  const log = [];
  if (M.winner(st) !== null) return log;

  fireReady(st, log);
  if (M.winner(st) !== null) return log;

  // Spend the treasury first (research, defenses, up to two units) but keep half the AP for the army.
  const half = Math.ceil(M.rule(st, "apPerTurn") / 2);
  maybeResearch(st, log);
  maybeDefend(st, log);
  maybeProduce(st, log, half);
  maybeProduce(st, log, half);
  tryCoordinated(st, log);
  march(st, log);
  fireReady(st, log);
  if (M.winner(st) !== null) return log;

  maybeProduce(st, log);

  const ended = M.endTurn(st);
  log.push({ kind: "end", player: ended.player, income: ended.income, winner: ended.winner, volleys: ended.volleys });
  return log;
}

export function safePlay(st) {
  const sim = clone(st);
  try {
    return { ok: true, actions: playAiTurn(sim), state: sim };
  } catch (err) {
    return { ok: false, error: String(err.message || err), state: st };
  }
}
